
// The Boards v2 tour (IL-095) lives in the board bundle (web/board/src/
// onboarding): a practice board in memory, with the tour over it. This page
// is the seam, as BoardPage is for a board: it decides who the tour is for
// (a new account, someone's first board, or taken again), loads the bundle,
// mounts it into a <body>-level portal, keeps the person's progress on their
// user doc, and carries out how it ends (their first board made and opened,
// the board it was for opened, or back where they came from).
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import { db, auth, functions, storage, FieldValue, FieldPath } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { analytics, EVENTS } from '@/services/analytics';
import { emailMatchesFallbackEnterpriseDomain } from '@/utils/enterpriseFallbackDomains';
import { rememberBoardsVersion } from '@/utils/boardsVersion';
import {
	BOARDS_TOUR_ID, boardsTourRequired, boardsTourState, canSkipBoardsTour, nextBoardsTourState, safeReturnPath, snoozeBoardsTour,
} from '@/utils/boardsTour';
import type { MountedOnboarding, TourExit, TourProgress, TourReason } from '@board-app';
import MarkLoader from '@/components/base/MarkLoader.vue';

/** What a new account's first board is called, until they rename it. */
const FIRST_BOARD_TITLE = 'My first board';

export default Vue.extend({
	name: 'BoardsTourPage',
	components: {
		MarkLoader,
	},
	mixins: [TeamUserMixin],
	metaInfo: {
		title: 'Welcome to Boards | RunDiffusion',
	},
	data () {
		return {
			loadError: null as string | null,
			tour: null as MountedOnboarding | null,
			mountStarted: false,
			portal: null as HTMLElement | null,
			prevHtmlOverflow: '',
			prevBodyOverscroll: '',
			destroyed: false,
			reason: null as TourReason | null,
		};
	},
	computed: {
		...mapState(['user', 'authUser', 'team', 'runnitState']),
		...mapGetters(['displayLabels', 'skipNewUserSurvey', 'companyClubOfferBenefits']),
		isTeamMode (): boolean {
			return !!(this.runnitState && this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM);
		},
		boardId (): string | null {
			const id = this.$route.query.board;
			return typeof id === 'string' && id ? id : null;
		},
		/** an enterprise account: by its company's plan, a verified domain claim, or the fallback domains */
		enterprise (): boolean {
			return !!(
				(this.isTeamMode && this.team && this.companyClubOfferBenefits && this.companyClubOfferBenefits.isEnterprise) ||
				this.skipNewUserSurvey ||
				(this.user && emailMatchesFallbackEnterpriseDomain(this.user.email))
			);
		},
	},
	watch: {
		user () {
			if (!this.mountStarted) this.start();
		},
	},
	mounted () {
		const html: HTMLElement = document.documentElement;
		this.prevHtmlOverflow = html.style.overflow;
		this.prevBodyOverscroll = document.body.style.overscrollBehavior;
		html.style.overflow = 'hidden';
		document.body.style.overscrollBehavior = 'none';
		this.start();
	},
	beforeDestroy () {
		this.destroyed = true;
		this.teardown();
		document.documentElement.style.overflow = this.prevHtmlOverflow;
		document.body.style.overscrollBehavior = this.prevBodyOverscroll;
	},
	methods: {
		/** Once the account has loaded: who the tour is for, then the tour. */
		start () {
			if (this.destroyed || this.mountStarted || !this.user || !this.user.id) return;
			const required = boardsTourRequired(this.user);
			const asked = this.$route.query.reason;
			this.reason = required ? 'new-account' : asked === 'first-board' && this.boardId ? 'first-board' : 'replay';
			void this.mount();
		},
		async boardRef (): Promise<{ id: string, title: string } | null> {
			if (!this.boardId || this.reason === 'new-account') return null;
			try {
				const snap = await db.doc(`runnits/${this.boardId}`).get();
				const data = snap.exists ? snap.data() : null;
				return data && !data.deletedAt ? { id: this.boardId, title: (data.title || '').trim() || 'your board' } : null;
			} catch (e) {
				console.error('[tour] could not read the board', e);
				return null;
			}
		},
		async mount () {
			this.mountStarted = true;
			this.loadError = null;
			try {
				const [mod, board] = await Promise.all([
					import(/* webpackChunkName: "board-app" */ '@board-app'),
					this.boardRef(),
				]);
				if (this.destroyed) return;
				const portal: HTMLElement = document.createElement('div');
				portal.className = 'board-page-portal';
				document.body.appendChild(portal);
				this.portal = portal;
				const reason = this.reason as TourReason;
				const required = reason === 'new-account';
				this.tour = mod.mountOnboarding(portal, {
					user: {
						uid: this.authUser ? this.authUser.uid : this.user.id,
						name: this.user.name || this.user.displayName || '',
						email: this.user.email || '',
						isAdmin: !!this.user.isAdmin,
					},
					reason,
					canSkip: canSkipBoardsTour({ user: this.user, enterprise: this.enterprise, required }),
					board,
					labels: { team: this.displayLabels.team },
					onProgress: (p: TourProgress) => this.onProgress(p),
					onExit: (exit: TourExit) => this.onExit(exit, board, mod),
				});
			} catch (e) {
				console.error('[tour] failed to mount', e);
				this.loadError = (e && (e as Error).message) || 'Unknown error';
			}
		},
		/** The person's progress, on their user doc (beside the app's other tours), and in analytics. */
		onProgress (p: TourProgress) {
			const next = nextBoardsTourState(boardsTourState(this.user), p.status);
			if (this.user && this.user.id) {
				db.doc(`users/${this.user.id}`).set({
					featureToursCompletedMap: { [BOARDS_TOUR_ID]: { ...next, updatedAt: FieldValue.serverTimestamp() } },
				}, { merge: true }).catch((e: unknown) => console.error('[tour] could not keep its progress', e));
			}
			const event = p.status === 'started' ? EVENTS.BOARDS_TOUR_STARTED : p.status === 'completed' ? EVENTS.BOARDS_TOUR_COMPLETED : EVENTS.BOARDS_TOUR_SKIPPED;
			analytics.track(event, { reason: this.reason, step: p.step });
		},
		/** How it ended: their first board made and opened, the board it was for opened, or back. */
		async onExit (exit: TourExit, board: { id: string, title: string } | null, mod: typeof import('@board-app')) {
			if (exit === 'failed') {
				// its practice board never opened: on into the app (a new account is asked again next visit)
				this.teardown();
				this.loadError = 'Its practice board couldn\'t open in this browser.';
				return;
			}
			if (exit === 'create-board') {
				const id = await mod.createCanvasBoard(
					{ db, auth, functions, storage, FieldValue, FieldPath },
					{
						uid: this.authUser ? this.authUser.uid : this.user.id,
						name: this.user.name || this.user.displayName || '',
						email: this.user.email || '',
						isAdmin: !!this.user.isAdmin,
						teamId: this.isTeamMode && this.team ? this.team.id : null,
					},
					FIRST_BOARD_TITLE,
				).catch((e: unknown) => {
					console.error('[tour] could not make the first board', e);
					throw new Error('Your first board couldn\'t be made just now. Please try again.');
				});
				rememberBoardsVersion('v2');
				this.$router.replace({ name: ROUTER.BOARD, params: { boardId: id } }).catch(() => {});
				return;
			}
			if (board && (exit === 'open-board' || exit === 'skip')) {
				rememberBoardsVersion('v2');
				this.$router.replace({ name: ROUTER.BOARD, params: { boardId: board.id } }).catch(() => {});
				return;
			}
			const back = safeReturnPath(this.$route.query.back);
			if (back) this.$router.replace(back).catch(() => {});
			else this.$router.replace({ name: ROUTER.RUNNIT_BOARDS_V2_PAGE }).catch(() => {});
		},
		teardown () {
			this.mountStarted = false;
			if (this.tour) {
				try {
					this.tour.unmount();
				} catch (e) {
					console.error('[tour] failed to unmount cleanly', e);
				}
				this.tour = null;
			}
			if (this.portal && this.portal.parentNode) this.portal.parentNode.removeChild(this.portal);
			this.portal = null;
		},
		reload () {
			this.teardown();
			this.start();
		},
		/** The tour can't load: on into the app for now (a new account is asked again next visit). */
		later () {
			snoozeBoardsTour();
			this.$router.replace({ name: ROUTER.RUNNITS_HOME }).catch(() => {});
		},
	},
});
